import React, { useMemo, useState } from "react";
import { X, Eye, Wallet, AlertTriangle } from "lucide-react";
import { supabase } from "../../../Services/supabaseClient";
import "./fixedAssets.css";
import { formatNaira } from "./faMoney";

function monthInputDefault() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${yyyy}-${mm}-01`;
}

export default function PostDepreciationModal({
  companyId,
  onClose,
  onPosted,
}) {
  const [periodMonth, setPeriodMonth] = useState(monthInputDefault());
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const warningLines = useMemo(() => {
    if (!preview?.lines) return [];
    return preview.lines.filter(
      (l) => Array.isArray(l.warnings) && l.warnings.length > 0
    );
  }, [preview]);

  async function callFn(fnName, payload) {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    if (!token) throw new Error("Not authenticated.");

    const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/${fnName}`;

    const res = await fetch(fnUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const json = await res.json();
    if (!res.ok) throw new Error(json?.error || "Request failed.");
    return json;
  }

  async function runPreview() {
    setErr("");
    setBusy(true);
    setPreview(null);

    try {
      if (!companyId)
        throw new Error(
          "Company ID is missing. Pass companyId into PostDepreciationModal."
        );

      const json = await callFn("fixed-assets-preview-depreciation", {
        company_id: companyId,
        period_month: periodMonth,
      });

      setPreview(json);
    } catch (e) {
      setErr(e.message || "Error");
    } finally {
      setBusy(false);
    }
  }

  async function post() {
    setErr("");
    setBusy(true);

    try {
      if (!companyId)
        throw new Error(
          "Company ID is missing. Pass companyId into PostDepreciationModal."
        );

      const ok = window.confirm(
        `Post depreciation for ${String(periodMonth).slice(
          0,
          7
        )}?\n\nThis will create journal entries in your ledger.`
      );
      if (!ok) return;

      const json = await callFn("fixed-assets-post-depreciation", {
        company_id: companyId,
        period_month: periodMonth,
      });

      onPosted?.(json);
    } catch (e) {
      setErr(e.message || "Error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.97)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 14,
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        className="fa-panel"
        style={{ width: "min(980px, 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{ display: "flex", justifyContent: "space-between", gap: 10 }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>
              Depreciation — Preview & Post
            </div>
            <div style={{ opacity: 0.8, fontSize: 13, marginTop: 4 }}>
              Preview calculates depreciation without posting. Post writes
              journal entries to your ledger.
            </div>
          </div>
          <button className="fa-icon-btn" onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        <div className="fa-toolbar" style={{ marginTop: 12 }}>
          <div style={{ minWidth: 220 }}>
            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
              Period Month
            </div>
            <input
              className="fa-input"
              type="date"
              value={periodMonth}
              onChange={(e) => setPeriodMonth(e.target.value)}
            />
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            <button className="fa-btn" onClick={runPreview} disabled={busy}>
              <Eye size={16} /> {busy ? "Loading…" : "Preview"}
            </button>
            <button
              className="fa-btn fa-btn-primary"
              onClick={post}
              disabled={busy || !preview || preview?.posted_already}
            >
              <Wallet size={16} /> Post
            </button>
          </div>
        </div>

        {err && (
          <div style={{ marginTop: 10, color: "#ffb4b4", fontSize: 13 }}>
            {err}
          </div>
        )}

        {!preview && (
          <div className="fa-card" style={{ marginTop: 12 }}>
            <div style={{ opacity: 0.85 }}>
              Click <b>Preview</b> to calculate depreciation for the selected
              period.
            </div>
          </div>
        )}

        {preview && (
          <>
            <div className="fa-grid" style={{ marginTop: 12 }}>
              <div className="fa-card">
                <div className="label">Posting date</div>
                <div className="value">{preview.posting_date}</div>
                <div className="hint">Last day of month</div>
              </div>
              <div className="fa-card">
                <div className="label">Assets considered</div>
                <div className="value">{preview.totals.assets}</div>
                <div className="hint">Active + in service</div>
              </div>
              <div className="fa-card">
                <div className="label">Eligible</div>
                <div className="value">{preview.totals.eligible}</div>
                <div className="hint">Depreciation &gt; 0</div>
              </div>
              <div className="fa-card">
                <div className="label">Depreciation (month)</div>
                <div className="value">
                  {formatNaira(preview.totals.depreciation)}
                </div>
                <div className="hint">Total for period</div>
              </div>
            </div>

            {preview.posted_already && (
              <div className="fa-card" style={{ marginTop: 12 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <AlertTriangle size={18} />
                  <div>
                    <div style={{ fontWeight: 800 }}>
                      Already posted for this period
                    </div>
                    <div style={{ opacity: 0.85, marginTop: 4, fontSize: 13 }}>
                      Posting is disabled. Use the <b>Reversal</b> feature if
                      this was posted incorrectly.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {warningLines.length > 0 && (
              <div className="fa-card" style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 900, marginBottom: 6 }}>
                  Warnings ({warningLines.length})
                </div>
                <div style={{ opacity: 0.85, fontSize: 13 }}>
                  You can still post, but review mappings and asset settings to
                  avoid incorrect ledger entries.
                </div>
              </div>
            )}

            <div className="fa-panel" style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 900, marginBottom: 10 }}>
                Preview Lines
              </div>
              <table className="fa-table">
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th>Category</th>
                    <th>Base</th>
                    <th>Remaining</th>
                    <th>Monthly</th>
                    <th>This Period</th>
                    <th>Warnings</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lines.map((l) => (
                    <tr key={l.asset_id}>
                      <td>
                        <div style={{ fontWeight: 800 }}>{l.asset_tag}</div>
                        <div style={{ opacity: 0.8, fontSize: 12 }}>
                          {l.name}
                        </div>
                      </td>
                      <td>{l.category || "-"}</td>
                      <td>{formatNaira(l.base)}</td>
                      <td>{formatNaira(l.remaining)}</td>
                      <td>{formatNaira(l.monthly_amount)}</td>
                      <td style={{ fontWeight: 900 }}>
                        {formatNaira(l.depreciation_amount)}
                      </td>
                      <td style={{ fontSize: 12, opacity: 0.85 }}>
                        {l.warnings?.length ? l.warnings.join(" • ") : "—"}
                      </td>
                    </tr>
                  ))}

                  {preview.lines.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ opacity: 0.75 }}>
                        No assets available for this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
